import { get } from 'lodash';

/**
 * ImagePropertiesType
 */
type ImagePropertiesType = {
  extension: string;
  key: string;
  type: string;
  width?: number | string;
  height?: number | string;
};

/**
 * ImageOptionsType
 */
type ImageOptionsType = {
  type?: string;
  width?: string;
  height?: string;
};

enum BlogArticleType {
  POST = 'post',
  GUIDE = 'guide',
}

/**
 * DEFAULT_IMAGE_OPTIONS
 */
const DEFAULT_IMAGE_OPTIONS = {
  width: '560',
  height: 'AUTO',
  type: 'main-master',
};

/**
 * Localization
 */
const DEFAULT_LOCALE = 'en-GB';
const LOCALES_CONFIG = {
  'en-GB': {
    uris: {
      art: 'art',
      artDetail: 'art',
      artists: 'artists',
      artistDetail: 'artist',
      artistArt: 'art',
      cartDetail: 'cart',
      eventDetail: 'event',
      offerDetail: 'me/offer',
      sellerArtDetail: 'art/edit',
      sellerJoin: 'join',
      sellerOfferDetail: 'offer',
      adminOfferDetail: 'offer',
      adminOfferTabs: { logs: 'logs' },
      resetPassword: 'password/reset',
      collectionDetail: 'collection',
      guide: 'guide',
      guides: 'guides',
      article: 'article',
      articles: 'articles',
    },
    query: {
      cart: {
        visitorId: '_vid',
      },
      sellerJoin: {
        code: 'code',
        locale: 'locale',
      },
      sellerOffer: {
        action: 'action',
      },
      resetPassword: {
        code: 'code',
      },
    },
  },
  'de-DE': {
    uris: {
      art: 'de/kunst',
      artDetail: 'de/kunst',
      artists: 'de/kuenstler',
      artistDetail: 'de/kuenstler',
      artistArt: 'kunst',
      cartDetail: 'de/warenkorb',
      eventDetail: 'de/veranstaltung',
      offerDetail: 'de/ich/angebot',
      sellerArtDetail: 'art/edit',
      sellerJoin: 'join',
      sellerOfferDetail: 'offer',
      adminOfferDetail: 'offer',
      adminOfferTabs: { logs: 'logs' },
      resetPassword: 'de/passwort/zuruecksetzen',
      collectionDetail: 'de/sammlung',
      guide: 'de/leitfaden',
      guides: 'de/leitfaeden',
      article: 'de/post',
      articles: 'de/artikel',
    },
    query: {
      cart: {
        visitorId: '_vid',
      },
      sellerJoin: {
        code: 'code',
        locale: 'locale',
      },
      sellerOffer: {
        action: 'action',
      },
      resetPassword: {
        code: 'code',
      },
    },
  },
  'fr-FR': {
    uris: {
      art: 'fr/art',
      artDetail: 'fr/art',
      artists: 'fr/artistes',
      artistDetail: 'fr/artiste',
      artistArt: 'art',
      cartDetail: 'fr/panier',
      eventDetail: 'fr/evenements',
      offerDetail: 'fr/moi/offrir',
      sellerArtDetail: 'art/edit',
      sellerJoin: 'join',
      sellerOfferDetail: 'offer',
      adminOfferDetail: 'offer',
      adminOfferTabs: { logs: 'logs' },
      resetPassword: 'fr/mot-de-passe/reinitialiser',
      collectionDetail: 'fr/collection',
      guide: 'fr/guide',
      guides: 'fr/guides',
      article: 'fr/article',
      articles: 'fr/articles',
    },
    query: {
      cart: {
        visitorId: '_vid',
      },
      sellerJoin: {
        code: 'code',
        locale: 'locale',
      },
      sellerOffer: {
        action: 'action',
      },
      resetPassword: {
        code: 'code',
      },
    },
  },
};

/**
 * getLocaleConfig
 *
 * @param {string} key
 * @param {string} locale
 * @returns {any}
 */
const getLocaleConfig = (key: string, locale: string = DEFAULT_LOCALE) => {
  const config = LOCALES_CONFIG[locale] || LOCALES_CONFIG[DEFAULT_LOCALE] || {};
  return get(config, key, null);
};

/**
 * UrlAssembler
 */
class UrlAssembler {
  /**
   * buildQueryString: Build a URL query string from object
   *
   * @param {Record<string, number | string>} queryString
   * @returns {string}
   */
  public static buildQueryString(queryString: Record<string, number | string>): string {
    if (queryString) {
      const qs = Object.keys(queryString).reduce(
        (accumulator, name) => [...accumulator, `${encodeURIComponent(name)}=${encodeURIComponent(queryString[name])}`],
        [],
      );
      return qs.length > 0 ? `?${qs.join('&')}` : '';
    } else {
      return '';
    }
  }

  /**
   * image: Generate thumbnail URL for image object
   *
   * @param {string} baseUrl
   * @param {Array<ImagePropertiesType>} images
   * @param {ImageOptionsType} options
   * @returns {string | null | undefined}
   */
  public static image(baseUrl: string, images: Array<ImagePropertiesType>, options?: ImageOptionsType): string {
    if (!images || !images.length) {
      return;
    }

    const { type, width, height } = options ? { ...DEFAULT_IMAGE_OPTIONS, ...options } : DEFAULT_IMAGE_OPTIONS;

    const imageProps: ImagePropertiesType =
      images && images.find((image: ImagePropertiesType): boolean => image.type === type);

    if (!imageProps) {
      return;
    }

    const { extension, key } = imageProps;

    return baseUrl && width && height && key && extension
      ? `${baseUrl.replace(/\/+$/, '')}/${width}x${height}_${key}.${extension}`
      : null;
  }

  /**
   * artworks list: Generate URL for artworks list page
   *
   * @param {string} baseUrl
   * @param {string} locale
   * @returns {string | null}
   */
  public static artworksList(baseUrl: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.artDetail', locale)].join('/') : null;
  }

  /**
   * artwork: Generate URL for artwork detail page
   *
   * @param {string} baseUrl
   * @param {string} id
   * @param {string} slug
   * @param {string} locale
   * @returns {string | null}
   */
  public static artwork(baseUrl: string, id: string, slug: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && id && slug
      ? [
          baseUrl.replace(/\/+$/, ''),
          getLocaleConfig('uris.artDetail', locale),
          encodeURIComponent(id),
          encodeURIComponent(slug),
        ].join('/')
      : null;
  }

  /**
   * cart: Generate URL for cart detail page
   *
   * @param {string} baseUrl
   * @param {number} visitorId
   * @param {string} locale
   * @param {Record<string, number | string>} queryString
   * @returns {string | null}
   */
  public static cart(
    baseUrl: string,
    visitorId?: number,
    queryString?: Record<string, number | string>,
    locale: string = DEFAULT_LOCALE,
  ): string | null {
    const qs = UrlAssembler.buildQueryString({
      ...queryString,
      ...(visitorId ? { [getLocaleConfig('query.cart.visitorId', locale)]: visitorId } : {}),
    });
    return baseUrl
      ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.cartDetail', locale)].join('/') + (qs ? qs : '')
      : null;
  }

  /**
   * artists list: Generate URL for artists list page
   *
   * @param {string} baseUrl
   * @param {string} locale
   * @returns {string | null}
   */
  public static artistsList(baseUrl: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.artists', locale)].join('/') : null;
  }

  /**
   * artist: Generate URL for artwork detail page
   *
   * @param {string} baseUrl
   * @param {string} id
   * @param {string} alias
   * @param {string} locale
   * @returns {string | null}
   */
  public static artist(baseUrl: string, id: string, alias: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && id && alias
      ? [
          baseUrl.replace(/\/+$/, ''),
          getLocaleConfig('uris.artistDetail', locale),
          encodeURIComponent(id),
          encodeURIComponent(alias),
        ].join('/')
      : null;
  }

  /**
   * artist art: Generate URL for artwork art page
   *
   * @param {string} baseUrl
   * @param {string} id
   * @param {string} alias
   * @param {string} locale
   * @returns {string | null}
   */
  public static artistArt(baseUrl: string, id: string, alias: string, locale: string = DEFAULT_LOCALE): string | null {
    const artistUrl = UrlAssembler.artist(baseUrl, id, alias, locale);

    return artistUrl ? [artistUrl, getLocaleConfig('uris.artistArt', locale)].join('/') : null;
  }

  /**
   * event: Generate URL for event detail page
   *
   * @param {string} baseUrl
   * @param {string | number} id
   * @param {string} slug
   * @param {string} locale
   * @returns {string | null}
   */
  public static event(
    baseUrl: string,
    id: string | number,
    slug: string,
    locale: string = DEFAULT_LOCALE,
  ): string | null {
    return baseUrl && id && slug
      ? [
          baseUrl.replace(/\/+$/, ''),
          getLocaleConfig('uris.eventDetail', locale),
          encodeURIComponent(id),
          encodeURIComponent(slug),
        ].join('/')
      : null;
  }

  /**
   * collection: Generate URL for artwork detail page
   *
   * @param {string} baseUrl
   * @param {string} id
   * @param {string} alias
   * @param {string} locale
   * @returns {string | null}
   */
  public static collection(baseUrl: string, id: string, slug: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && id && slug
      ? [
          baseUrl.replace(/\/+$/, ''),
          getLocaleConfig('uris.collectionDetail', locale),
          encodeURIComponent(id),
          encodeURIComponent(slug),
        ].join('/')
      : null;
  }

  /**
   * offer: Generate URL for offer detail page
   *
   * @param {string} baseUrl
   * @param {string | number} id
   * @param {string} locale
   * @returns {string | null}
   */
  public static offer(baseUrl: string, id: string | number, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && id
      ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.offerDetail', locale), encodeURIComponent(id)].join('/')
      : null;
  }

  /**
   * sellerJoinUrl: Generate URL for seller join
   *
   * @param {string} baseUrl
   * @param {string} code
   * @param {string} locale
   * @returns {string | null}
   */
  public static sellerJoinUrl(baseUrl: string, code: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && code
      ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.sellerJoin', locale)].join('/') +
          UrlAssembler.buildQueryString({
            [getLocaleConfig('query.sellerJoin.locale', locale)]: locale,
            [getLocaleConfig('query.sellerJoin.code', locale)]: code,
          })
      : null;
  }

  /**
   * sellerOffer: Generate URL for seller offer detail
   *
   * @param {string} baseUrl
   * @param {string | number} id
   * @param {string} action,
   * @param {string} locale
   * @returns {string | null}
   */
  public static sellerOffer(
    baseUrl: string,
    id: string | number,
    action?: string,
    locale: string = DEFAULT_LOCALE,
  ): string | null {
    return baseUrl && id
      ? `${[
          baseUrl.replace(/\/+$/, ''),
          getLocaleConfig('uris.sellerOfferDetail', locale),
          encodeURIComponent(id),
        ].join('/')}${
          action
            ? UrlAssembler.buildQueryString({
                [getLocaleConfig('query.sellerOffer.action', locale)]: action,
              })
            : ''
        }`
      : null;
  }

  /**
   * sellerArtwork: Generate URL for seller art detail
   *
   * @param {string} baseUrl
   * @param {string | number} id
   * @param {string} locale
   * @returns {string | null}
   */
  public static sellerArtwork(baseUrl: string, id: string | number, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && id
      ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.sellerArtDetail', locale), encodeURIComponent(id)].join('/')
      : null;
  }

  /**
   * adminOfferDetail: Generate URL for admin console offer detail
   *
   * @param {string} baseUrl
   * @param {string | number} id
   * @param {string} action,
   * @param {string} locale
   * @returns {string | null}
   */
  public static adminOfferDetail(
    baseUrl: string,
    id: string | number,
    tab = 'logs',
    locale: string = DEFAULT_LOCALE,
  ): string | null {
    const localizedTab = (tab && getLocaleConfig(`uris.adminOfferTabs.${tab}`, locale)) || null;

    return baseUrl && id && (!tab || (tab && localizedTab))
      ? `${[
          baseUrl.replace(/\/+$/, ''),
          getLocaleConfig('uris.adminOfferDetail', locale),
          encodeURIComponent(id),
          ...(localizedTab ? [encodeURIComponent(localizedTab)] : []),
        ].join('/')}`
      : null;
  }

  /**
   * resetPasswordUrl: Generate URL for reset password page
   *
   * @param {string} baseUrl
   * @param {string} code
   * @param {string} locale
   * @returns {string | null}
   */
  public static resetPasswordUrl(baseUrl: string, code: string, locale: string = DEFAULT_LOCALE): string | null {
    return baseUrl && code
      ? [baseUrl.replace(/\/+$/, ''), getLocaleConfig('uris.resetPassword', locale)].join('/') +
          UrlAssembler.buildQueryString({
            [getLocaleConfig('query.resetPassword.code', locale)]: code,
          })
      : null;
  }

  /**
   * article
   *
   * @param {string} baseUrl
   * @param {string} id
   * @param {string} slug
   * @param {string} type
   * @param {string} locale
   * @returns {string | null}
   */
  public static article(
    baseUrl: string,
    id: string,
    slug: string,
    type: string,
    locale: string = DEFAULT_LOCALE,
  ): string | null {
    let path = null;

    switch (type) {
      case BlogArticleType.POST:
        path = getLocaleConfig('uris.article', locale);
        break;
      case BlogArticleType.GUIDE:
        path = getLocaleConfig('uris.guide', locale);
        break;
    }

    return baseUrl && id && path
      ? [baseUrl.replace(/\/+$/, ''), path, encodeURIComponent(id), encodeURIComponent(slug)].join('/')
      : null;
  }

  /**
   * articleCategory
   *
   * @param {string} baseUrl
   * @param {string} category
   * @param {string} type
   * @param {string} locale
   * @returns {string | null}
   */
  public static articleCategory(
    baseUrl: string,
    category: string,
    type: string,
    locale: string = DEFAULT_LOCALE,
  ): string | null {
    let path = null;

    switch (type) {
      case BlogArticleType.POST:
        path = getLocaleConfig('uris.articles', locale);
        break;
      case BlogArticleType.GUIDE:
        path = getLocaleConfig('uris.guides', locale);
        break;
    }

    return baseUrl && path ? [baseUrl.replace(/\/+$/, ''), path, encodeURIComponent(category)].join('/') : null;
  }
}

export type { ImagePropertiesType, ImageOptionsType };

export { UrlAssembler };
